import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import type { OrganizationAiModelsResponse } from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Accès au catalogue des modèles d'IA de l'organisation + sélection du modèle actif.
 *
 * - `getOrganizationAiModel` (query) : catalogue + modèle courant.
 * - `assignOrganizationAiModel` (mutation) : le backend renvoie le même payload que le GET,
 *   on réutilise donc la réponse pour rafraîchir le cache (seed via setQueryData),
 *   sans refetch réseau supplémentaire.
 */
const useOrganizationAiModel = () => {
    const { iaManager } = useSdk();
    const queryClient = useQueryClient();

    const fetchModels = async (): Promise<OrganizationAiModelsResponse> => {
        return iaManager.getOrganizationAiModel();
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: queryKeys.organizationAiModel(),
        queryFn: fetchModels,
        staleTime: STALE_TIME.stable,
    });

    const { mutateAsync: assignOrganizationAiModel, isPending: isUpdating } =
        useMutation({
            mutationFn: async (modelId: string) => {
                return iaManager.assignOrganizationAiModel(modelId);
            },
            // Le PATCH renvoie le même payload que le GET : on réhydrate directement le
            // cache avec les résultats reçus (la page se réaffiche sans refetch).
            onSuccess: (response) => {
                queryClient.setQueryData(
                    queryKeys.organizationAiModel(),
                    response,
                );
            },
        });

    return {
        availableModels: data?.availableModels ?? [],
        currentModelId: data?.currentModelId,
        isLoading,
        isError,
        isUpdating,
        assignOrganizationAiModel,
    };
};

export default useOrganizationAiModel;
